import { userSettingsAddLayer, userSettingsRemoveLayer, userSettingsSwapLayers } from './user-settings-actions'
import { fetchVendorObjects } from './vendor-actions'
import { activeVendorsAdd, activeVendorsDelete } from './active-vendors'
import { setUserPreferences } from './user-preferences'
import { showLayers } from './layers-visible-actions'
import { showMapObjects, hideMapObjects } from '../lib/util'
import { isBasemapLayer, DEFAULT_BASEMAP_LAYER_ID } from '../lib/layer-access'
import { isTimeLayer } from '../lib/timeLayers/timeline'

// What tapping a layer tile in the layer menu does, moved out of the Layer component so
// Artemis's set_layer_visibility action runs exactly the same steps (WEB-7093): one
// basemap at a time, unit-filter layers replacing each other, vendor objects shown or
// hidden, overlay companions (inside userSettingsAddLayer/RemoveLayer), and the eye
// toggle turned back on when a data layer is turned on.
//
// One weather layer at a time, like basemaps (matched with iOS and Android): turning on a
// layer with a Layer/Get timeStep turns every other active one off, in the same save, so
// the menu, the time bar's layer row, Artemis and deep links all obey it.
//
// active: whether the tile showed the layer as on; defaults to its place in the active
// layers setting, which is what every tile passes. fromFavorites: the tap came from the
// favourites list, which skips the redundant remove before an add, as the tile always has.

const VENDOR_MEASURES = ['lines', 'markers', 'photos', 'tracks', 'circles', 'polygons']

const activeLayerIds = state => {
  const setting = state.userSettings.find(s => s.settingId === 1)
  return setting ? setting.valueArray : []
}

export const layerMenuTap = (layer, { settingId = 1, fromFavorites = false, active } = {}) => (dispatch, getState) => {
  const state = getState(),
    activeLayers = activeLayerIds(state),
    { layersDictionary, vendors } = state,
    mapReference = typeof state.mapReference === 'function' ? state.mapReference() : null,
    isOn = active !== undefined ? active : activeLayers.indexOf(layer.id) !== -1

  if (isOn) {
    if (!isBasemapLayer(layer)) {
      dispatch(userSettingsRemoveLayer(settingId, layer.id))
      if (layer.vendorId) {
        dispatch(activeVendorsDelete(layer.vendorId))
        const vendor = vendors[layer.vendorId]
        if (vendor && vendor['objects']) {
          VENDOR_MEASURES.forEach(type => hideMapObjects(vendor['objects'][type]))
        }
      }
    } else {
      dispatch(userSettingsRemoveLayer(settingId, layer.id))
        .then(() => {
          dispatch(userSettingsAddLayer(settingId, DEFAULT_BASEMAP_LAYER_ID))
        })
    }
    return
  }

  if (!isBasemapLayer(layer)) {
    // Turning a data layer on while the eye toggle is off would add it to the
    // active list and draw nothing, so the tap reads as broken. Switching a
    // basemap is exempt — that is the map underneath, which the eye never hid.
    dispatch(showLayers())
    const otherWeather = isTimeLayer(layer)
      ? activeLayers.filter(id => id !== layer.id && isTimeLayer(layersDictionary[id]))
      : []
    // The swap below is one save; a remove sent ahead of it could land after it.
    if (!fromFavorites && !otherWeather.length) {
      dispatch(userSettingsRemoveLayer(settingId, layer.id))
    }
    const filterable = activeLayers.find(id => layersDictionary[id] && layersDictionary[id].filterOnUnit)
    if (otherWeather.length) {
      dispatch(userSettingsSwapLayers(settingId, otherWeather, layer.id))
    } else if (layer.filterOnUnit && filterable) {
      dispatch(userSettingsRemoveLayer(settingId, filterable))
        .then(() => {
          dispatch(userSettingsAddLayer(settingId, layer.id))
        })
    } else {
      if (layer.vendorId && !vendors[layer.vendorId]) {
        dispatch(fetchVendorObjects(layer))
          .then(() => {
            dispatch(activeVendorsAdd(layer.vendorId))
          })
      } else if (vendors[layer.vendorId]) {
        dispatch(activeVendorsAdd(layer.vendorId))
        const vendor = vendors[layer.vendorId]
        if (vendor && vendor['objects']) {
          VENDOR_MEASURES.forEach(type => showMapObjects(vendor['objects'][type], mapReference))
        }
      }
      dispatch(userSettingsAddLayer(settingId, layer.id))
    }
  } else {
    const basemap = activeLayers.find(id => layersDictionary[id] && isBasemapLayer(layersDictionary[id]) && id !== layer.id)
    dispatch(userSettingsRemoveLayer(settingId, basemap))
      .then(() => {
        dispatch(setUserPreferences({ selectedTopo: layer.id }))
        dispatch(userSettingsAddLayer(settingId, layer.id))
      })
  }
}
